import logger from './logger.js';
import { fetchRegions } from './regionUtils.js';
import { PROD_ENV } from './constants/environments.js';
import NavigationService from './NavigationService.js';
import { subscribe, emit, events } from './eventManager.js';
import { extractLocaleStringFromPath } from './utils/localeUtils.js';
/**
 * Name of the localStorage key used to store the user's selected region.
 */
const REGION_STORAGE_KEY = 'regionSelector.region';
/**
 * Name of the localStorage key used to store the fetched regions data.
 */
const REGIONS_DATA_STORAGE_KEY = 'regionSelector.regionsData';
/**
 * Name of the localStorage key holding when the regions data was fetched (epoch milliseconds).
 * Kept separate from REGIONS_DATA_STORAGE_KEY so that key stays a plain array, which older
 * versions of this module running on the same origin still expect.
 */
const REGIONS_DATA_FETCHED_AT_STORAGE_KEY = 'regionSelector.regionsDataFetchedAt';
/**
 * How long a cached regions list is used before it is refreshed from the API (24 hours).
 */
const REGIONS_DATA_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/**
 * Default region (GLOBAL) initially set to ensure a region is selected.
 */
const DEFAULT_REGION = ['WW'];
/**
 * Name of the query string parameter used to override the region.
 */
const REGION_QUERY_PARAM = 'region';
/**
 * Controls how the initial region is geo-detected when no region is stored in localStorage.
 * - 'ip' (default): async IP-based detection via account.subscribeToVisitor (Ninetailed/country module).
 *   Used by Eleventy-powered sites.
 * - 'locale': derives country from the locale code in the URL path (e.g. '/en-US/...' → country 'US').
 *   Used by Astro-powered (server-rendered) sites where the locale is deterministic per request.
 */
export const REGION_DETECTION_MODE = {
    IP: 'ip',
    LOCALE: 'locale',
};
/**
 * The region that the query string parameter resolved to on this page load, or null if the
 * parameter was absent or has already been stripped from the URL.
 *
 * Acts as a one-shot flag for stripRegionQueryParamAfterApply(): it is set in initializeRegion()
 * immediately before the parameter is applied, and cleared by the first setRegion() call
 * afterwards, which is the one that removes the parameter from the address bar. Later calls in
 * the same page load then find it null and leave the URL alone.
 */
let queryStringRegion = null;
/**
 * Tracks the active region for the module.
 */
let region = DEFAULT_REGION;
/**
 * ASEAN member countries
 */
const ASEAN_COUNTRIES = ['BN', 'KH', 'ID', 'LA', 'MY', 'MM', 'PH', 'SG', 'TH', 'VN'];
/**
 * Maps a country code to its corresponding region.
 *
 * @param {string} countryCode - Two-character ISO country code (e.g., 'US', 'DE', 'JP')
 * @returns {string[] | undefined} The mapped region array or undefined if not found
 *
 * @example
 *     const region = mapCountryToRegion('US');
 *     // Returns ['US']
 *
 * @example
 *     const region = mapCountryToRegion('PH');
 *     // Returns ['PH', 'ASEAN']
 */
export function mapCountryToRegion(countryCode) {
    if (!countryCode || typeof countryCode !== 'string') {
        return undefined;
    }
    const upperCountryCode = countryCode.toUpperCase();
    // Check if country is an ASEAN member
    if (ASEAN_COUNTRIES.includes(upperCountryCode)) {
        return [upperCountryCode, 'ASEAN'];
    }
    // Return country code as single-value array for all other countries
    return [upperCountryCode];
}
/**
 * Reads the region query string parameter and resolves it to a region array.
 *
 * When the supported regions list is available it is used to validate the value: a recognized
 * value resolves to that region's array, and an unrecognized one falls back to DEFAULT_REGION
 * (['WW']).
 *
 * When the list is *not* available the value is still honored, resolved through
 * mapCountryToRegion() instead of being discarded. This matters because initializeRegion() no
 * longer waits for the regions fetch before resolving the region (see its comment), so on a
 * first-ever visit the list is legitimately not cached yet. Dropping the parameter there would
 * leave the browser showing a different region than the server rendered: siemens-com's
 * viewer-request Lambda@Edge forwards ?region= to Ninetailed unvalidated, and ?region= is part
 * of the CloudFront cache key, so the HTML already reflects it. mapCountryToRegion() is the same
 * mapping the edge uses, so both sides resolve the value identically.
 *
 * Returns null only if the parameter is absent, or if it is present but resolves to nothing.
 */
function getRegionFromQueryString() {
    const search = NavigationService.getSearch();
    if (!search)
        return null;
    const params = new URLSearchParams(search);
    const rawValue = params.get(REGION_QUERY_PARAM);
    if (!rawValue)
        return null;
    const upperValue = rawValue.toUpperCase();
    const regionsData = getRegions();
    // Length check, not just truthiness: fetchRegions() resolves to [] on failure, and an empty
    // array can validate nothing — treat it as "list unavailable" and fall through below.
    if (regionsData && regionsData.length > 0) {
        const match = regionsData.find(r => r.region.some(code => code.toUpperCase() === upperValue));
        if (match) {
            logger.log(`Region from query string: ${JSON.stringify(match.region)}`);
            return match.region;
        }
        // Parameter present but not recognized — fall back to Global
        logger.log(`Unrecognized region query param "${rawValue}", falling back to Global (WW)`);
        return [...DEFAULT_REGION];
    }
    // Regions list not available yet (first visit, or a failed fetch) — resolve the value the
    // same way the edge does rather than ignoring it
    const mappedRegion = mapCountryToRegion(upperValue);
    if (mappedRegion) {
        logger.log(`Region data unavailable; applying region query param "${rawValue}" unvalidated`);
        return mappedRegion;
    }
    logger.log(`Region data unavailable; ignoring region query param "${rawValue}"`);
    return null;
}
/**
 * Strips the region query string parameter from the address bar as soon as it has been applied.
 *
 * ?region= is a one-shot override — an external SiePortal link, or a QA/testing convenience.
 * Once its value has taken effect the visitor's region lives in their Ninetailed profile (the
 * edge writes the trait when it sees the parameter) and in localStorage, not in the URL. Leaving
 * it in the address bar sets a trap: pressing Back re-lands on a URL that still carries it, and
 * the edge re-applies it, silently reverting a region the visitor has since changed. Stripping
 * unconditionally, right after the one time the value is read, removes that trap entirely.
 *
 * Does nothing when the URL did not supply a region on this page load, so a region change made
 * without the parameter present never touches the URL. Also does nothing on a second call for the
 * same page load (queryStringRegion is cleared after the first strip), so it is safe to call from
 * every setRegion() invocation without re-checking history state each time.
 */
function stripRegionQueryParamAfterApply() {
    // The URL never asked for a region on this page load, so there is nothing to strip
    if (!queryStringRegion)
        return;
    removeRegionQueryParam();
    // Already stripped — nothing left for a later setRegion() call this page load to repeat
    queryStringRegion = null;
}
/**
 * Strips the region query string parameter from the address bar.
 *
 * history.replaceState() is used so no extra browser history entry is created and the page is
 * not reloaded. Any other query parameters and the hash fragment are preserved.
 *
 * Does nothing if the parameter is absent or if there is no window (SSR).
 */
function removeRegionQueryParam() {
    const search = NavigationService.getSearch();
    if (!search)
        return;
    const params = new URLSearchParams(search);
    if (!params.has(REGION_QUERY_PARAM))
        return;
    params.delete(REGION_QUERY_PARAM);
    const remaining = params.toString();
    const newUrl = `${NavigationService.getPathname()}${remaining ? `?${remaining}` : ''}${NavigationService.getHash()}`;
    NavigationService.replaceState(null, '', newUrl);
    logger.log(`Removed "${REGION_QUERY_PARAM}" query param from URL: ${newUrl}`);
}
/**
 * Sets the user's region from the locale code in the current URL path, when no region is
 * already stored in localStorage. Extracts the country subtag from a BCP-47 locale
 * (e.g. '/en-US/...' → 'US') and maps it to a region via mapCountryToRegion(). This is the
 * 'locale' geoDetectionMode alternative to IP-based detection.
 *
 * Runs synchronously against the current path. There is nothing to wait for: initializing the
 * locales module never changes the URL, so the path cannot gain a locale later in the page load.
 * If the path has no locale, no region is set here.
 */
function handleLocaleDetection() {
    if (_getLocally())
        return;
    const localeCode = extractLocaleStringFromPath(NavigationService.getPathname());
    if (!localeCode)
        return;
    const parts = localeCode.split('-');
    const countryCode = parts[parts.length - 1];
    const mappedRegion = mapCountryToRegion(countryCode);
    if (mappedRegion) {
        logger.log(`Setting region from locale "${localeCode}" → country "${countryCode}"`);
        // Not awaited on purpose: detection must not block initializeRegion(). setRegion() never
        // rejects (see its doc comment), so there is no rejection to handle here.
        void setRegion(mappedRegion);
    }
}
/**
 * Initializes the region setting based on user preferences and progressive logic.
 *
 * This function determines the user's region using a progressive approach:
 * 1. Previously stored region preference
 * 2. User account information (if authenticated) — IP mode only
 * 3. Country-based region detection (IP mode: via visitor country; locale mode: via URL locale)
 * 4. Default region (fallback)
 *
 * @param {RegionConfig} config - Configuration options
 * @param {string} [config.env] - Environment to use for API calls
 * @param {RegionDetectionMode} [config.geoDetectionMode] - How to geo-detect the initial region
 * @returns {Promise<void>} A promise that resolves when the region has been initialized
 *
 * @example
 *     await initializeRegion({ env: 'dev' });
 *
 * @example Locale mode (Astro SSR sites)
 *     await initializeRegion({ env: 'prod', geoDetectionMode: 'locale' });
 */
export async function initializeRegion(config = {}) {
    // Set up environment configuration
    const { env = PROD_ENV, geoDetectionMode = REGION_DETECTION_MODE.IP } = config;
    if (geoDetectionMode === REGION_DETECTION_MODE.LOCALE) {
        handleLocaleDetection();
    }
    else {
        window?.disw.account.subscribeToVisitor(handleCountry);
    }
    // Resolve the active region and notify downstream systems (chiefly the Ninetailed identify
    // call) *before* fetching the region picker's country/name list below. That fetch only
    // supplies display data for the dropdown UI — it has no bearing on which region is active —
    // so it must not gate how soon personalization learns the region. Awaiting it first meant a
    // slow or failing taxonomy request (its fetch has a 10s timeout) delayed identify() well past
    // the initial render, leaving personalization keyed off an undefined region trait even when
    // the region was already known from localStorage.
    //
    // Query string takes unconditional precedence over localStorage and
    // IP-based geolocation. setRegion() writes to localStorage, so the
    // selected region carries across subsequent page navigations.
    // handleCountry()'s existing _getLocally() guard then prevents
    // IP detection from overwriting it — no additional guard is needed.
    const queryStringRegionValue = getRegionFromQueryString();
    if (queryStringRegionValue !== null) {
        // Record what the URL asked for before applying it, so the setRegion() call below knows
        // to strip the now-redundant parameter from the address bar
        queryStringRegion = queryStringRegionValue;
        await setRegion(queryStringRegionValue);
    }
    else {
        // Get the region through progressive approach
        loadRegion();
    }
    await loadRegionsData(env);
    // Guard against the browser's back-forward cache (bfcache) serving a stale,
    // server-personalized page. When the browser restores a page from bfcache, it
    // resurrects the entire frozen JS heap and DOM exactly as they were — this
    // module's in-memory `region` variable included — with zero network request, so
    // any SSR-rendered personalization on that page stays keyed to whatever region
    // was active when it was originally rendered. If the region has since changed
    // (e.g. switched on a different page, then Back), the restored page is now
    // showing content for the wrong region until a real reload happens.
    //
    // `pageshow` with `event.persisted === true` fires specifically on a bfcache
    // restore (not on a normal load, where `initializeRegion()` already runs fresh).
    // Comparing the frozen `region` variable to a fresh, direct localStorage read
    // (bypassing the frozen value) detects exactly this mismatch. This is cheap and
    // only does anything on the rare bfcache-restore path — ordinary navigation and
    // bfcache restores where the region hasn't changed are unaffected, so this
    // doesn't trade away bfcache's performance benefit in the common case.
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('pageshow', handleBfcacheRestore);
    }
}
/**
 * Reloads the page if it was restored from the browser's back-forward cache (bfcache) with a
 * region that's since gone stale. See the comment in initializeRegion() for why this is needed.
 *
 * @param {PageTransitionEvent} event - The pageshow event
 */
function handleBfcacheRestore(event) {
    if (!event.persisted) {
        return;
    }
    const freshRegion = _getLocally();
    if (freshRegion && JSON.stringify(freshRegion) !== JSON.stringify(region)) {
        logger.log(`Region changed since this page was cached (was ${JSON.stringify(region)}, now ${JSON.stringify(freshRegion)}) — reloading to refresh personalized content`);
        NavigationService.reload();
    }
}
/**
 * This function handles a change to the user's country either from the account
 * module's subscribeToVisitor function or from the country module, but only if
 * we don't already know the user's country.
 *
 * @param visitor the optional Visitor object which contains the user's country
 */
function handleCountry(visitor) {
    // We only set the user's region from a callback if we don't already know it
    if (_getLocally()) {
        return;
    }
    // Used to skip IP being used for debugging of global region
    // if (visitor) {
    //     visitor.country = null;
    // }
    let countryCode = '';
    // First get the country of the visitor if we have one
    if (visitor?.country) {
        countryCode = visitor.country;
        logger.log('Getting country from visitor session', countryCode);
    }
    else {
        // Otherwise, use the country module
        logger.log('Getting country from country module', countryCode);
        const country = window?.disw?.country?.getCountry();
        if (country?.countrycode) {
            countryCode = country.countrycode;
        }
        // If default property is found, use global instead of the US default.
        // NOTE: Global also has countryCode WW
        if (country.default) {
            countryCode = 'WW';
            logger.log('Country was set to default, using GLOBAL instead with value WW');
        }
    }
    const mappedRegion = mapCountryToRegion(countryCode);
    // Set the region if we get one mapped from the country
    if (mappedRegion) {
        setRegion(mappedRegion);
    }
}
/**
 * Loads the regions data through a progressive approach. First it checks localStorage, then it
 * loads the data from the API and stores it in localStorage. A cached list older than
 * REGIONS_DATA_MAX_AGE_MS is still used immediately, then refreshed in the background.
 *
 * @param env the environment in which the module is operating
 */
async function loadRegionsData(env) {
    const existingRegionsData = _getRegionsDataLocally();
    // Length check, not just truthiness: an empty array is truthy but never represents a real,
    // complete regions list — fetchRegions() only resolves to [] when something went wrong
    // (missing API key, a failed or timed-out request). Treating that as "already loaded" cached
    // the failure forever, so every later page load found the same empty array and never retried,
    // permanently showing "No regions available" until someone cleared that one localStorage key.
    // Requiring a non-empty result lets a transient failure self-heal on the next page load.
    const hasCachedRegionsData = !!existingRegionsData && existingRegionsData.length > 0;
    if (hasCachedRegionsData) {
        logger.log('Using existing regions data from localStorage');
        // Emit on the cache-hit path too: a consumer that mounted before this ran (and found
        // nothing yet in getRegions()) still needs to hear that the list is available.
        emit(events.REGIONS_DATA, existingRegionsData, false);
        if (!_isRegionsDataStale()) {
            return;
        }
        logger.log('Cached regions data is stale, refreshing');
    }
    try {
        const regionsData = await fetchRegions(env);
        // Keep a stale-but-real list rather than replacing it with a failed (empty) result
        if (hasCachedRegionsData && regionsData.length === 0) {
            return;
        }
        _storeRegionsData(regionsData);
        logger.log('Successfully fetched and stored regions data');
        // Notify subscribers (e.g. the region dropdown) that the list is ready. getRegions()
        // is a synchronous read of localStorage at call time and has no way to signal "the
        // data just changed", so callers that mounted while this fetch was in flight need
        // this event to know when to re-read it. Previously nothing was emitted here, and the
        // dropdown stayed on "No regions available" until some unrelated events.REGION
        // emission happened to trigger a re-check.
        emit(events.REGIONS_DATA, regionsData, false);
    }
    catch (error) {
        logger.warn(`Failed to fetch regions data: ${error}`);
    }
}
export function getRegions() {
    return _getRegionsDataLocally();
}
/**
 * Gets the current region. Returns a copy to avoid unintended changes. Assumes that loadRegion()
 * has been called first, which happens in initializeRegion.
 *
 * @returns {string[]} The current region as an array of strings
 *
 * @example
 *     const region = getRegion();
 *     // Returns ['US'] or similar
 */
export function getRegion() {
    return [...region]; // Return a copy to prevent external modification
}
/**
 * Loads the current region using authoritative, progressive lookup logic.
 *
 * Progressive logic:
 * 1. Check local storage for previously selected region
 * 2. Wait for visitor data from account module (IP-based geolocation)
 * 3. If visitor indicates default country, use Global (WW) as fallback
 *
 * This function encapsulates the authoritative logic for getting the user's region
 * utilizing an event-driven approach to avoid race conditions.
 *
 * @example
 *     loadRegion();
 *     Restores the region from localStorage if available, otherwise waits for visitor event
 */
function loadRegion() {
    const storedRegion = _getLocally();
    if (storedRegion && region != storedRegion) {
        region = storedRegion;
        emit(events.REGION, storedRegion, false);
        // Called directly rather than via a subscribe(events.REGION, ...) hook: initializeRegion()
        // no longer registers one, so that resolving the region is not coupled to event delivery
        // (emit() defers callbacks through setTimeout).
        void invokeIdentifyOnRegionChange(storedRegion);
    }
}
/**
 * Sets the region for the user session.
 *
 * This function sets the region and stores it for future use.
 * It also emits a region change event to notify subscribers.
 *
 * Resolves once the change has been propagated to Ninetailed. Callers that follow this with a
 * hard page reload (to get regionalized SSR content) should await it first. Note this only
 * sequences the JS promise chain, though — it does not guarantee the underlying identify request
 * reached Ninetailed before the page unloads, since the SDK posts it via a plain fetch() with no
 * `keepalive`, which navigation can abort mid-flight. See invokeIdentifyOnRegionChange()'s doc
 * comment for why ?region= remains the durable channel for page-reload flows regardless.
 *
 * The returned promise never rejects: invokeIdentifyOnRegionChange() swallows and logs its own
 * failures. Several callers (handleCountry(), initializeRegion()'s fallback path) invoke this
 * without awaiting, and an unhandled rejection from one of those would surface far from its cause.
 *
 * @param {string[]} newRegion - The region to set as an array of strings
 * @returns {Promise<void>}
 *
 * @example
 *     await setRegion(['Europe', 'Germany']);
 */
export async function setRegion(newRegion) {
    if (!Array.isArray(newRegion) || newRegion.length === 0) {
        logger.warn('Invalid region provided to setRegion. Region must be a non-empty array.');
        return;
    }
    // Store the region locally
    _storeLocally(newRegion);
    // Update the current region
    region = [...newRegion];
    // If this page load applied a ?region= override, strip it from the URL now that it has taken
    // effect — see stripRegionQueryParamAfterApply()'s doc comment
    stripRegionQueryParamAfterApply();
    // Emit the region change event
    emit(events.REGION, region, false);
    logger.log(`Region set to: ${JSON.stringify(region)}`);
    await invokeIdentifyOnRegionChange(region);
}
/**
 * Invokes the identify call that sends the region change to Ninetailed for regionalization.
 *
 * Segment is deliberately not sent an identify call here. Its region trait was not used by
 * anyone and was only noise, and Ninetailed is all regionalization needs (XDDD-4376).
 *
 * Resolves once the identify call completes, or immediately if the Ninetailed SDK is not present,
 * so callers that need the change visible to a subsequent server round-trip can await it.
 *
 * Never rejects — a failing identify() is logged and swallowed. Some call sites (loadRegion())
 * deliberately do not await this, and an unhandled rejection from one of those would surface far
 * from its cause. Regionalization is best-effort: a failed identify must not break page load or
 * block a pending reload.
 *
 * window.ninetailed is assigned by the Ninetailed SDK itself (its constructor registers window
 * handlers unconditionally), so this is not a no-op on the Astro SSR sites (siemens-com) either,
 * once their NinetailedProvider has mounted and hydrated client-side. A resolved call here is a
 * real, durable write to the visitor's Ninetailed profile (a non-preflight upsertProfile under the
 * hood), not just local state.
 *
 * It is still not a substitute for the server-side upsertProfile call siemens-com's edge/middleware
 * makes for a region carried by ?region= or derived for a brand-new visitor: those happen before
 * any client-side hydration, so there is no window.ninetailed yet to call this against. And even
 * for an explicit in-session region switch, a caller that immediately follows this with a hard
 * reload (see setRegion()) is not guaranteed the identify request reached the server first — the
 * SDK's fetch has no `keepalive`, so navigation can abort it in flight. ?region= on the reload
 * gives the server a second, durable channel that does not depend on winning that race.
 */
async function invokeIdentifyOnRegionChange(region) {
    const ninetailed = window.ninetailed;
    if (!ninetailed)
        return;
    try {
        await ninetailed.identify(window.disw?.account?.getAccount()?.sid, { enriched: { region } });
    }
    catch (error) {
        logger.warn(`Failed to propagate region change to Ninetailed: ${error}`);
    }
}
/**
 * Subscribes to region changes.
 *
 * This function enables other modules or components to be notified
 * whenever changes are made to the region.
 *
 * @param {CallbackFunction} callback - Function to call when region changes
 * @returns {() => void} Unsubscribe function
 *
 * @example
 *     const unsubscribe = subscribeToRegion((newRegion, error) => {
 *         console.log('Region changed to:', newRegion);
 *     });
 *
 *     // Later, to unsubscribe:
 *     unsubscribe();
 */
export function subscribeToRegion(callback) {
    return subscribe(events.REGION, callback);
}
/**
 * Subscribes to the list of selectable regions becoming available.
 *
 * Distinct from subscribeToRegion: that fires when the user's *current* region changes, this
 * fires when the *list of selectable regions* finishes loading from the taxonomy API (see
 * loadRegionsData()). A caller that reads getRegions() before that fetch completes has no other
 * way to learn when to re-read it.
 *
 * Safe to call at any time relative to the load: subscribe() replays the last emitted value, so
 * a subscriber that registers after the list has already loaded is still invoked immediately.
 *
 * @param {CallbackFunction} callback - Function to call with the loaded regions list
 * @returns {() => void} Unsubscribe function
 *
 * @example
 *     const unsubscribe = subscribeToRegionsData((regionsData) => {
 *         console.log('Regions list loaded:', regionsData);
 *     });
 *
 *     // Later, to unsubscribe:
 *     unsubscribe();
 */
export function subscribeToRegionsData(callback) {
    return subscribe(events.REGIONS_DATA, callback);
}
/**
 * Store region in localStorage
 *
 * @param {string[]} regionData - An array representing the region hierarchy
 * @example
 *     _storeLocally(['North America', 'United States'])
 */
function _storeLocally(regionData) {
    logger.log(`Storing region locally in localStorage: ${JSON.stringify(regionData)}`);
    try {
        // Store value as a JSON string in localStorage
        localStorage.setItem(REGION_STORAGE_KEY, JSON.stringify(regionData));
    }
    catch (error) {
        logger.warn(`Failed to store region in localStorage: ${error}`);
    }
}
/**
 * Get the region array from localStorage.
 *
 * @returns {string[] | null} Returns null if nothing stored locally
 */
function _getLocally() {
    try {
        const regionString = localStorage.getItem(REGION_STORAGE_KEY);
        if (regionString) {
            // Parse the JSON string back to an array
            const parsedRegion = JSON.parse(regionString);
            if (Array.isArray(parsedRegion)) {
                return parsedRegion;
            }
        }
    }
    catch (error) {
        logger.warn(`Failed to retrieve or parse stored region: ${error}`);
    }
    return null;
}
/**
 * Get the regions data from localStorage.
 *
 * @returns {Regions | null} Returns null if nothing stored locally
 */
function _getRegionsDataLocally() {
    try {
        const regionsDataString = localStorage.getItem(REGIONS_DATA_STORAGE_KEY);
        if (regionsDataString) {
            // Parse the JSON string back to an array
            const parsedRegionsData = JSON.parse(regionsDataString);
            if (_isValidRegionsData(parsedRegionsData)) {
                return parsedRegionsData;
            }
            logger.warn('Discarding invalid regions data from localStorage');
        }
    }
    catch (error) {
        logger.warn(`Failed to retrieve or parse stored regions data: ${error}`);
    }
    return null;
}
/**
 * Checks that stored regions data has the shape callers rely on: an array of regions whose
 * region codes are all strings.
 *
 * The cached list is never refreshed once stored, so a list fetched while taxonomy returned a
 * country without a countryCode (cached as region: [null]) would otherwise break region lookups
 * on every page load. Treating it as invalid makes loadRegionsData() fetch a fresh copy.
 *
 * @param {unknown} regionsData - The parsed localStorage value
 * @returns {boolean} True if the data can be used as Regions
 */
function _isValidRegionsData(regionsData) {
    return Array.isArray(regionsData) && regionsData.every(r => Array.isArray(r?.region) && r.region.every((code) => typeof code === 'string'));
}
/**
 * Checks whether the cached regions data is older than REGIONS_DATA_MAX_AGE_MS. Data with no
 * recorded fetch time (cached before it was tracked) counts as stale, so it refreshes once.
 *
 * @returns {boolean} True if the cached regions data should be refreshed
 */
function _isRegionsDataStale() {
    try {
        const fetchedAt = Number(localStorage.getItem(REGIONS_DATA_FETCHED_AT_STORAGE_KEY));
        return !fetchedAt || Date.now() - fetchedAt > REGIONS_DATA_MAX_AGE_MS;
    }
    catch {
        return true;
    }
}
/**
 * Store regions data in localStorage
 *
 * @param {Regions} regionsData - The regions data fetched from the API
 * @example
 *     _storeRegionsData([{name: 'United States', countryCode: 'US', ...}])
 */
function _storeRegionsData(regionsData) {
    logger.log(`Storing regions data in localStorage: ${regionsData.length} regions`);
    try {
        // Store value as a JSON string in localStorage
        localStorage.setItem(REGIONS_DATA_STORAGE_KEY, JSON.stringify(regionsData));
        localStorage.setItem(REGIONS_DATA_FETCHED_AT_STORAGE_KEY, String(Date.now()));
    }
    catch (error) {
        logger.warn(`Failed to store regions data in localStorage: ${error}`);
    }
}
/**
 * Get the ASEAN member countries
 * @returns {string[]} Array of ASEAN country codes
 */
function getAseanCountryCodes() {
    return ASEAN_COUNTRIES;
}
// Export the default object with the main functions
export default {
    getRegions,
    getRegion,
    setRegion,
    subscribeToRegion,
    subscribeToRegionsData,
    mapCountryToRegion,
    getAseanCountryCodes,
    REGION_DETECTION_MODE,
};
